import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { calculatorService, clientService, api } from '../../services/api';
import MobileGlobalStyles from './MobileStyles';
import { TpvDrawer } from '../TPV';
import { useAuth } from '../../contexts/AuthContext';
import { clientDiscountSuffix } from '../../utils/clientDisplay';
import { clientesModoTienda } from '../../utils/clientSearch';

// Animation for TPV button
const pulse = keyframes`
  0% {
    transform: translateX(-50%) scale(1);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
  }
  50% {
    transform: translateX(-50%) scale(1.05);
    box-shadow: 0 4px 20px rgba(40, 167, 69, 0.6);
  }
  100% {
    transform: translateX(-50%) scale(1);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
  }
`;

// Mobile-first container
const MobileContainer = styled.div`
  background: #f5f5f5;
  padding-bottom: 16px;
  position: relative;
  overflow-x: hidden;
  width: 100%;
`;

// Sticky header for mobile
const MobileHeader = styled.div`
  background: white;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

// Collapsible sections for mobile
const Section = styled.div`
  background: white;
  margin: 8px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SectionHeader = styled.button`
  width: 100%;
  padding: 16px;
  background: ${props => props.active ? '#2196F3' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:active {
    background: ${props => props.active ? '#1976D2' : '#f5f5f5'};
  }
  
  .icon {
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.3s ease;
  }
`;

const SectionContent = styled.div`
  padding: ${props => props.expanded ? '16px' : '0'};
  max-height: ${props => props.expanded ? '2000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

// Mobile-optimized form inputs
const MobileInput = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  input, select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 16px; // Prevents zoom on iOS
    background: white;
    -webkit-appearance: none; // Remove iOS styling
    
    &:focus {
      outline: none;
      border-color: #2196F3;
      box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
    }
  }
  
  select {
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 20px;
    padding-right: 40px;
  }
`;

// Material card for mobile
const MaterialCard = styled.div`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  
  .material-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  
  .material-name {
    font-weight: 500;
    color: #333;
  }
  
  .material-price {
    color: #2196F3;
    font-weight: 600;
  }
  
  .material-details {
    font-size: 14px;
    color: #666;
  }
  
  .remove-btn {
    background: #ff5252;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    margin-top: 8px;
    width: 100%;
  }
`;

// Mobile action buttons container - En el flujo del documento
const MobileActionButtons = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px;
  margin: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

// Action button for mobile - Full width style
const FloatingButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s ease;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.clear {
    background: #607D8B;
    color: white;
  }

  &.calculate {
    background: #2196F3;
    color: white;
    flex: 1.5;
  }

  &.calculate-highlight {
    background: #4CAF50;
    color: white;
    flex: 1.5;
  }

  &.save {
    background: #4CAF50;
    color: white;
  }

  &.tpv {
    background: #FF9800;
    color: white;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
    opacity: 0.9;
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

// Price summary - En el flujo del documento
const PriceSummaryFooter = styled.div`
  background: white;
  margin: 8px;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-bottom: 6px;

    &.total {
      font-size: 22px;
      font-weight: 700;
      color: #2196F3;
      margin-bottom: 0;
      margin-top: 8px;
      padding-top: 12px;
      border-top: 2px solid #e0e0e0;
    }
  }
`;

// Modal for material selection
const MaterialModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 2000;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: flex-end;
  
  .modal-content {
    background: white;
    width: 100%;
    height: 70vh;
    max-height: 600px;
    border-radius: 16px 16px 0 0;
    overflow: hidden;
    animation: ${props => props.show ? 'slideUp 0.3s ease' : 'none'};
    box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
    display: flex;
    flex-direction: column;
    
    @media (max-height: 700px) {
      height: 80vh;
    }
    
    @media (orientation: landscape) {
      height: 90vh;
      max-height: none;
    }
  }
  
  .modal-header {
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    flex-shrink: 0;
  }
  
  .modal-body {
    padding: 16px;
    overflow-y: auto;
    flex: 1;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    min-height: 0;
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

// Search input for material modal
const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 16px;
`;

// Material option in modal
const MaterialOption = styled.div`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    background: #f5f5f5;
    transform: scale(0.98);
  }
  
  .name {
    font-weight: 500;
    margin-bottom: 4px;
  }
  
  .details {
    font-size: 14px;
    color: #666;
    display: flex;
    justify-content: space-between;
  }
  
  .price {
    color: #2196F3;
    font-weight: 600;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  background: white;
  border-radius: 12px;
  padding: 4px;
  margin: 8px;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const ModeButton = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: ${props => props.active ? '#2196F3' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:active {
    transform: scale(0.98);
  }
`;

const CalculatorMobile = () => {
  const { user, isAdmin } = useAuth();
  const [mode, setMode] = useState('professional'); // 'professional' or 'store'
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [, setTicketItems] = useState([]);
  const [addToTicketSignal, setAddToTicketSignal] = useState(0);
  const [tpvDrawerOpen, setTpvDrawerOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    materials: false,
    summary: false
  });
  const [materialModal, setMaterialModal] = useState({
    show: false,
    type: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    clientId: '',
    measurementType: 'INT',
    width: '',
    height: '',
    units: 1,
    discount: 0,
    clientReference: '',
    supplierReference: '',
    notes: ''
  });
  
  // Materials state
  const [molduras, setMolduras] = useState([]);
  const [passepartouts, setPassepartouts] = useState([]);
  const [cristales, setCristales] = useState([]);
  const [traseras, setTraseras] = useState([]);
  const [accesorios, setAccesorios] = useState([]);
  
  useEffect(() => {
    loadInitialData();
  }, []);
  
  useEffect(() => {
    const validAccesoriosMobile = accesorios.filter(a => a && (typeof a === 'string' ? a.trim() !== '' : !!(a.productId || a.id)));
    const onlyAccessories = validAccesoriosMobile.length > 0 && molduras.length === 0 && passepartouts.length === 0 && cristales.length === 0 && traseras.length === 0;
    const hasDimensions = formData.width && formData.height;
    if (formData.clientId && (hasDimensions || onlyAccessories)) {
      const hasAnyMaterial = molduras.length > 0 || passepartouts.length > 0 ||
                            cristales.length > 0 || traseras.length > 0 ||
                            accesorios.length > 0;
      if (hasAnyMaterial) {
        calculatePrice();
      }
    }
  }, [formData, molduras, passepartouts, cristales, traseras, accesorios]);

  // Force professional mode for non-admin users
  useEffect(() => {
    if (!isAdmin && mode === 'store') {
      setMode('professional');
    }
  }, [isAdmin, mode]);

  // Reload clients when mode changes (professional vs store/TPV)
  useEffect(() => {
    const loadClientsForMode = async () => {
      try {
        let clientsRes;

        if (mode === 'store') {
          // Venta tienda: particulares + la ficha "VENTA TIENDA". Se pide la lista completa
          // porque VENTA TIENDA tiene margen 70 (el de la tienda, correcto) y por la regla
          // "el tipo lo manda el margen" es profesional: con ?type=particular quedaba fuera,
          // no se preseleccionaba y sin cliente no se podían añadir artículos.
          const response = await api.get('/clients');
          const tienda = clientesModoTienda(response.data.clients);
          clientsRes = { clients: tienda.clients };

          // Auto-select "VENTA TIENDA" client if available
          if (tienda.ventaTienda) {
            setFormData(prev => ({ ...prev, clientId: tienda.ventaTienda.id }));
          }
        } else {
          // Professional mode: Load professional clients only
          clientsRes = await calculatorService.getClients();
        }

        setClients(clientsRes.clients || []);
      } catch (error) {
        console.error('Error loading clients for mode:', error);
        toast.error('Error al cargar clientes');
      }
    };

    loadClientsForMode();
  }, [mode]);

  const loadInitialData = async () => {
    try {
      const [clientsRes, productsRes] = await Promise.all([
        calculatorService.getClients(),
        calculatorService.getProducts()
      ]);

      console.log('Loaded clients:', clientsRes);
      console.log('Loaded products raw response:', productsRes);

      // Handle products - they might be nested in productsRes.products or be a productsByType object
      let processedProducts = {};

      if (productsRes.products) {
        processedProducts = productsRes.products;
      } else if (productsRes.productsByType) {
        processedProducts = productsRes.productsByType;
      } else {
        // Try to use the response directly
        processedProducts = productsRes;
      }

      console.log('Processed products:', processedProducts);
      setProducts(processedProducts);

      const clientsList = clientsRes.clients || [];

      // If user is NOT admin, find their client and show ONLY their client
      if (!isAdmin && user) {
        let userClient = clientsList.find(c => c.email === user.email);

        // If client list is empty, try fetching directly from API
        if (!userClient && clientsList.length === 0) {
          try {
            const refreshedClientsRes = await calculatorService.getClients();
            const refreshedClientsList = refreshedClientsRes.clients || [];
            userClient = refreshedClientsList.find(c => c.email === user.email);
          } catch (fetchError) {
            console.error('Error fetching clients:', fetchError);
          }
        }

        // For non-admin users, show ONLY their client in the dropdown
        if (userClient) {
          setClients([userClient]); // Only the user's client
          setFormData(prev => ({ ...prev, clientId: userClient.id }));
        } else {
          setClients([]); // No clients if user's client not found
          console.log('No client found for user email:', user.email);
        }
      } else if (isAdmin) {
        // Admin users see all clients
        setClients(clientsList);
        // Admin: Auto-select first client if only one
        if (clientsList.length === 1) {
          setFormData(prev => ({ ...prev, clientId: clientsList[0].id }));
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Error al cargar datos');
    }
  };

  // Helper to normalize decimal input (accept both comma and dot)
  const normalizeDecimal = (value) => {
    if (typeof value === 'string') {
      // Replace comma with dot for decimal separator
      return value.replace(',', '.');
    }
    return value;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const openMaterialModal = (type) => {
    console.log('Opening modal for:', type);
    console.log('Available products:', products);
    setMaterialModal({ show: true, type });
  };
  
  const closeMaterialModal = () => {
    setMaterialModal({ show: false, type: null });
    setSearchTerm(''); // Reset search when closing modal
  };
  
  const addMaterial = (type, product, extraData = {}) => {
    switch(type) {
      case 'moldura':
        setMolduras([...molduras, product.id]);
        break;
      case 'passepartout':
        setPassepartouts([...passepartouts, { productId: product.id, width: extraData.width || 0 }]);
        break;
      case 'cristal':
        setCristales([...cristales, product.id]);
        break;
      case 'trasera':
        setTraseras([...traseras, product.id]);
        break;
      case 'accesorio':
        setAccesorios([...accesorios, { productId: product.id, quantity: extraData.quantity || 1 }]);
        break;
    }
    closeMaterialModal();
  };
  
  const calculatePrice = async () => {
    console.log('🧮 calculatePrice function called!');
    try {
      setLoading(true);
      
      console.log('Calculating with:', {
        molduras,
        passepartouts,
        cristales,
        traseras,
        accesorios
      });
      
      const validAccesoriosCalc = accesorios.filter(a => a && (typeof a === 'string' ? a.trim() !== '' : !!(a.productId || a.id)));
      const onlyAccessories = validAccesoriosCalc.length > 0 && molduras.length === 0 && passepartouts.length === 0 && cristales.length === 0 && traseras.length === 0;

      const calculationData = {
        clientId: formData.clientId,
        measurementType: formData.measurementType,
        width: onlyAccessories && !formData.width ? 0 : parseFloat(formData.width),
        height: onlyAccessories && !formData.height ? 0 : parseFloat(formData.height),
        units: parseInt(formData.units),
        discount: parseFloat(formData.discount),
        notes: formData.notes,
        clientReference: formData.clientReference,
        supplierReference: formData.supplierReference,
        molduras: molduras, // Send array of IDs directly
        passepartouts: passepartouts, // Send array of objects with productId and width
        cristales: cristales, // Send array of IDs directly
        traseras: traseras, // Send array of IDs directly
        accesorios: accesorios, // Send array of IDs directly
        // In TPV/store mode, always apply 70% margin regardless of client
        overrideMargin: mode === 'store' ? 70 : null
      };
      
      console.log('Sending calculation data:', calculationData);
      
      const response = await calculatorService.calculate(calculationData);
      console.log('=== CALCULATION DEBUG ===');
      console.log('Full response:', response);
      console.log('response.calculation:', response.calculation);
      console.log('response.data:', response.data);
      console.log('Final calculation object:', response.calculation || response);
      
      const finalCalculation = response.calculation || response.data?.calculation || response.data || response;
      console.log('Setting calculation to:', finalCalculation);
      
      setCalculation(finalCalculation);
    } catch (error) {
      console.error('Error calculating price:', error);
      toast.error('Error al calcular precio: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const saveOrder = async () => {
    if (!calculation) return;

    const hasSelectedClient = Boolean(formData.clientId || calculation?.client?.id);
    if (!hasSelectedClient) {
      window.alert('Debes seleccionar un cliente antes de enviar el pedido.');
      return;
    }

    try {
      // Find client info (same as CalculatorPro)
      let clientInfo = calculation.client;
      if (!clientInfo || !clientInfo.id) {
        const selectedClient = clients.find(c => c.id === formData.clientId);
        if (!selectedClient) {
          window.alert('Debes seleccionar un cliente antes de enviar el pedido.');
          return;
        }
        clientInfo = {
          id: selectedClient.id,
          name: selectedClient.name,
          marginPercentage: selectedClient.marginPercentage || 0
        };
      }

      // Calculate amounts (same as CalculatorPro)
      const baseAmount = calculation.total || calculation.subtotal || 0;
      const taxAmount = 0; // PRO mode: no IVA on top
      const finalAmountWithVAT = baseAmount + taxAmount;

      // Generate fingerprint for proper grouping
      const calculationFingerprint = `${formData.measurementType || 'INT'}-${parseFloat(formData.width) || 0}x${parseFloat(formData.height) || 0}-${parseFloat(formData.discount) || 0}-${Date.now()}`;

      // Store original dimensions
      const originalDimensions = {
        width: parseFloat(formData.width) || 0,
        height: parseFloat(formData.height) || 0,
        measurementType: formData.measurementType || 'INT'
      };

      // Enrich items with metadata (same as CalculatorPro)
      const itemsWithMetadata = calculation.items?.map((item) => ({
        ...item,
        isPriceManual: false,
        notes: formData.notes || '',
        metadata: {
          ...(item.metadata || {}),
          fingerprint: calculationFingerprint,
          calculationIndex: 0,
          productName: item.productName || item.name || 'Artículo',
          originalWidth: originalDimensions.width,
          originalHeight: originalDimensions.height,
          originalMeasurementType: originalDimensions.measurementType
        }
      })) || [];

      const calculationWithClient = {
        ...calculation,
        items: itemsWithMetadata,
        fingerprint: calculationFingerprint,
        client: clientInfo,
        clientReference: formData.clientReference || calculation.clientReference,
        supplierReference: formData.supplierReference || calculation.supplierReference,
        notes: formData.notes || calculation.notes || '',
        subtotal: baseAmount,
        total: finalAmountWithVAT,
        tax: taxAmount,
        pricePerUnit: calculation.pricePerUnit,
        discount: calculation.discount,
        discountAmount: calculation.discountAmount
      };

      const orderData = {
        calculation: calculationWithClient,
        clientReference: formData.clientReference,
        supplierReference: formData.supplierReference,
        notes: formData.notes
      };

      const savedOrder = await calculatorService.saveOrder(orderData);
      toast.success('Pedido guardado exitosamente');
      
      // Store the order for TPV functionality
      setCurrentOrder(savedOrder.data);
      
      // Reset form
      setFormData({
        clientId: formData.clientId,
        measurementType: 'INT',
        width: '',
        height: '',
        units: 1,
        discount: 0,
        clientReference: '',
        supplierReference: '',
        notes: ''
      });
      setMolduras([]);
      setPassepartouts([]);
      setCristales([]);
      setTraseras([]);
      setAccesorios([]);
      setCalculation(null);
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Error al guardar pedido: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddToTicketFromCalculator = () => {
    if (!calculation) return;
    setAddToTicketSignal(prev => prev + 1);
  };
  
  const getMaterialCount = () => {
    return molduras.length + passepartouts.length + cristales.length +
           traseras.length + accesorios.length;
  };

  const clearForm = () => {
    setFormData({
      clientId: formData.clientId, // Mantener el cliente seleccionado
      measurementType: 'INT',
      width: '',
      height: '',
      units: 1,
      discount: 0,
      clientReference: '',
      supplierReference: '',
      notes: ''
    });
    setMolduras([]);
    setPassepartouts([]);
    setCristales([]);
    setTraseras([]);
    setAccesorios([]);
    setCalculation(null);
    toast.info('Formulario limpiado');
  };

  const isStoreAddMode = mode === 'store';

  const validAccesoriosForForm = accesorios.filter(a => a && (typeof a === 'string' ? a.trim() !== '' : !!(a.productId || a.id)));
  const onlyAccessoriesForForm = validAccesoriosForForm.length > 0 && molduras.length === 0 && passepartouts.length === 0 && cristales.length === 0 && traseras.length === 0;
  const dimensionsBlock = (!formData.width || !formData.height) && !onlyAccessoriesForForm;

  return (
    <>
      <MobileGlobalStyles />
      <MobileContainer>
        {isAdmin && (
          <ModeToggle>
            <ModeButton
              active={mode === 'professional'}
              onClick={() => setMode('professional')}
            >
              Calc. Profesional
            </ModeButton>
            <ModeButton
              active={mode === 'store'}
              onClick={() => setMode('store')}
            >
              Venta Tienda
            </ModeButton>
          </ModeToggle>
        )}

        <MobileHeader>
          <h3 style={{ margin: 0, fontSize: '18px' }}>
            {mode === 'store' ? 'Calculadora + TPV' : 'Calculadora de Marcos'}
          </h3>
        </MobileHeader>
        
        {/* Basic Data Section */}
        <Section>
        <SectionHeader 
          active={expandedSections.basic}
          expanded={expandedSections.basic}
          onClick={() => toggleSection('basic')}
        >
          <span>Datos Básicos</span>
          <span className="icon">▼</span>
        </SectionHeader>
        <SectionContent expanded={expandedSections.basic}>
          <MobileInput>
            <label>Cliente</label>
            <Select
              value={(() => {
                const selectedClient = clients.find(c => c.id === formData.clientId);
                if (selectedClient) {
                  return {
                    value: selectedClient.id,
                    label: `${selectedClient.name}${clientDiscountSuffix(selectedClient, isAdmin)} (${selectedClient.marginPercentage}%)${(selectedClient.odooArchived ? ' ⚠ archivado en Odoo' : '')}`
                  };
                }
                return null;
              })()}
              onChange={(selectedOption) => {
                setFormData({...formData, clientId: selectedOption?.value || ''});
              }}
              options={clients.map(client => ({
                value: client.id,
                label: `${client.name}${clientDiscountSuffix(client, isAdmin)} (${client.marginPercentage}%)${(client.odooArchived ? ' ⚠ archivado en Odoo' : '')}`
              }))}
              placeholder={clients.length === 0 ? "Cargando..." : "Buscar cliente..."}
              isClearable={isAdmin}
              isSearchable
              isDisabled={!isAdmin}
              noOptionsMessage={() => "No se encontraron clientes"}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: '44px',
                  fontSize: '16px',
                  borderColor: state.isFocused ? '#2196F3' : '#ddd',
                  boxShadow: state.isFocused ? '0 0 0 3px rgba(33, 150, 243, 0.1)' : 'none',
                  '&:hover': {
                    borderColor: '#2196F3'
                  }
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '2px 12px'
                }),
                input: (base) => ({
                  ...base,
                  fontSize: '16px'
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: '16px',
                  color: '#999'
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: '16px'
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999
                }),
                option: (base, state) => ({
                  ...base,
                  padding: '12px 16px',
                  fontSize: '16px',
                  backgroundColor: state.isSelected ? '#2196F3' : state.isFocused ? '#f5f5f5' : 'white',
                  color: state.isSelected ? 'white' : '#333'
                })
              }}
            />
          </MobileInput>

          <MobileInput>
            <label>Tipo de Medida</label>
            <select 
              value={formData.measurementType}
              onChange={(e) => setFormData({...formData, measurementType: e.target.value})}
            >
              <option value="INT">Interior</option>
              <option value="EXT">Exterior</option>
            </select>
          </MobileInput>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <MobileInput>
              <label>Ancho (cm)</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.width}
                onChange={(e) => setFormData({...formData, width: normalizeDecimal(e.target.value)})}
                placeholder="0.0"
              />
            </MobileInput>

            <MobileInput>
              <label>Alto (cm)</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.height}
                onChange={(e) => setFormData({...formData, height: normalizeDecimal(e.target.value)})}
                placeholder="0.0"
              />
            </MobileInput>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <MobileInput>
              <label>Unidades</label>
              <input 
                type="number"
                inputMode="numeric"
                value={formData.units}
                onChange={(e) => setFormData({...formData, units: parseInt(e.target.value) || 1})}
                min="1"
              />
            </MobileInput>
            
            <MobileInput>
              <label>Descuento (%)</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.discount}
                onChange={(e) => setFormData({...formData, discount: parseFloat(normalizeDecimal(e.target.value)) || 0})}
              />
            </MobileInput>
          </div>
        </SectionContent>
      </Section>
      
      {/* Materials Section */}
      <Section>
        <SectionHeader 
          active={getMaterialCount() > 0}
          expanded={expandedSections.materials}
          onClick={() => toggleSection('materials')}
        >
          <span>Materiales ({getMaterialCount()})</span>
          <span className="icon">▼</span>
        </SectionHeader>
        <SectionContent expanded={expandedSections.materials}>
          {/* Material buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
            <button 
              onClick={() => openMaterialModal('moldura')}
              style={{
                padding: '12px',
                border: '2px solid #2196F3',
                borderRadius: '8px',
                background: molduras.length > 0 ? '#2196F3' : 'white',
                color: molduras.length > 0 ? 'white' : '#2196F3',
                fontWeight: '500'
              }}
            >
              Molduras ({molduras.length})
            </button>
            
            <button 
              onClick={() => openMaterialModal('passepartout')}
              style={{
                padding: '12px',
                border: '2px solid #2196F3',
                borderRadius: '8px',
                background: passepartouts.length > 0 ? '#2196F3' : 'white',
                color: passepartouts.length > 0 ? 'white' : '#2196F3',
                fontWeight: '500'
              }}
            >
              Passepartouts ({passepartouts.length})
            </button>
            
            <button 
              onClick={() => openMaterialModal('cristal')}
              style={{
                padding: '12px',
                border: '2px solid #2196F3',
                borderRadius: '8px',
                background: cristales.length > 0 ? '#2196F3' : 'white',
                color: cristales.length > 0 ? 'white' : '#2196F3',
                fontWeight: '500'
              }}
            >
              Cristales ({cristales.length})
            </button>
            
            <button 
              onClick={() => openMaterialModal('trasera')}
              style={{
                padding: '12px',
                border: '2px solid #2196F3',
                borderRadius: '8px',
                background: traseras.length > 0 ? '#2196F3' : 'white',
                color: traseras.length > 0 ? 'white' : '#2196F3',
                fontWeight: '500'
              }}
            >
              Traseras ({traseras.length})
            </button>
            
            <button 
              onClick={() => openMaterialModal('accesorio')}
              style={{
                padding: '12px',
                border: '2px solid #2196F3',
                borderRadius: '8px',
                background: accesorios.length > 0 ? '#2196F3' : 'white',
                color: accesorios.length > 0 ? 'white' : '#2196F3',
                fontWeight: '500',
                gridColumn: 'span 2'
              }}
            >
              Accesorios ({accesorios.length})
            </button>
          </div>
          
          {/* Show selected materials */}
          {molduras.map((molduraId, index) => {
            const product = products.molduras?.find(p => p.id === molduraId);
            if (!product) return null;
            return (
              <MaterialCard key={`moldura-${molduraId}`}>
                <div className="material-header">
                  <span className="material-name">Moldura</span>
                  <span className="material-price"></span>
                </div>
                <div className="material-details">
                  {product.name}
                </div>
                <button 
                  className="remove-btn"
                  onClick={() => setMolduras(molduras.filter((_, i) => i !== index))}
                >
                  Eliminar
                </button>
              </MaterialCard>
            );
          })}
          
          {passepartouts.map((item, index) => {
            const product = products.passepartout?.find(p => p.id === item.productId);
            if (!product) return null;
            return (
              <MaterialCard key={`passepartout-${item.productId}`}>
                <div className="material-header">
                  <span className="material-name">Passepartout</span>
                  <span className="material-price"></span>
                </div>
                <div className="material-details">
                  {product.name} - {item.width}cm ventana
                </div>
                <button 
                  className="remove-btn"
                  onClick={() => setPassepartouts(passepartouts.filter((_, i) => i !== index))}
                >
                  Eliminar
                </button>
              </MaterialCard>
            );
          })}
          
          {cristales.map((cristalId, index) => {
            const product = products.cristales?.find(p => p.id === cristalId);
            if (!product) return null;
            return (
              <MaterialCard key={`cristal-${cristalId}`}>
                <div className="material-header">
                  <span className="material-name">Cristal</span>
                  <span className="material-price"></span>
                </div>
                <div className="material-details">
                  {product.name}
                </div>
                <button 
                  className="remove-btn"
                  onClick={() => setCristales(cristales.filter((_, i) => i !== index))}
                >
                  Eliminar
                </button>
              </MaterialCard>
            );
          })}
          
          {traseras.map((traseraId, index) => {
            const product = products.traseras?.find(p => p.id === traseraId);
            if (!product) return null;
            return (
              <MaterialCard key={`trasera-${traseraId}`}>
                <div className="material-header">
                  <span className="material-name">Trasera</span>
                  <span className="material-price"></span>
                </div>
                <div className="material-details">
                  {product.name}
                </div>
                <button 
                  className="remove-btn"
                  onClick={() => setTraseras(traseras.filter((_, i) => i !== index))}
                >
                  Eliminar
                </button>
              </MaterialCard>
            );
          })}
          
          {accesorios.map((item, index) => {
            const product = products.accesorios?.find(p => p.id === item.productId);
            if (!product) return null;
            return (
              <MaterialCard key={`accesorio-${item.productId || index}`}>
                <div className="material-header">
                  <span className="material-name">Accesorio</span>
                  <span className="material-price"></span>
                </div>
                <div className="material-details">
                  {product.name} x{item.quantity}
                </div>
                <button 
                  className="remove-btn"
                  onClick={() => setAccesorios(accesorios.filter((_, i) => i !== index))}
                >
                  Eliminar
                </button>
              </MaterialCard>
            );
          })}
        </SectionContent>
      </Section>
      
      {/* References Section */}
      <Section>
        <SectionHeader 
          expanded={expandedSections.references}
          onClick={() => toggleSection('references')}
        >
          <span>Referencias y Notas</span>
          <span className="icon">▼</span>
        </SectionHeader>
        <SectionContent expanded={expandedSections.references}>
          <MobileInput>
            <label>Ped. Cliente</label>
            <input 
              type="text"
              value={formData.clientReference}
              onChange={(e) => setFormData({...formData, clientReference: e.target.value})}
              placeholder="Opcional"
            />
          </MobileInput>
          
          <MobileInput>
            <label>Ped. Proveedor</label>
            <input 
              type="text"
              value={formData.supplierReference}
              onChange={(e) => setFormData({...formData, supplierReference: e.target.value})}
              placeholder="Opcional"
            />
          </MobileInput>
          
          <MobileInput>
            <label>Notas</label>
            <textarea 
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Notas adicionales..."
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '16px',
                resize: 'vertical'
              }}
            />
          </MobileInput>
        </SectionContent>
      </Section>
      
      {/* Price Summary Footer */}
      {calculation && (
        <PriceSummaryFooter>
          <div className="price-row">
            <span>Subtotal</span>
            <span>{calculation.subtotal?.toFixed(2)}€</span>
          </div>
          {formData.discount > 0 && (
            <div className="price-row">
              <span>Descuento ({formData.discount}%)</span>
              <span>-{calculation.discountAmount?.toFixed(2)}€</span>
            </div>
          )}
          <div className="price-row total">
            <span>Total</span>
            <span>{calculation.total?.toFixed(2)}€</span>
          </div>
          {calculation.fingerprint && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
              Huella: {calculation.fingerprint}
            </div>
          )}
        </PriceSummaryFooter>
      )}
      
      {/* Mobile Action Buttons */}
      <MobileActionButtons>
        {/* Botón Limpiar - Solo en modo profesional */}
        {mode === 'professional' && (
          <FloatingButton
            className="clear"
            onClick={clearForm}
            disabled={loading}
          >
            Limpiar
          </FloatingButton>
        )}

        {/* Botón Calcular */}
        <FloatingButton
          className={mode === 'store' && !calculation ? 'calculate-highlight' : 'calculate'}
          onClick={calculatePrice}
          disabled={loading || !formData.clientId || dimensionsBlock}
        >
          Calcular
        </FloatingButton>

        {/* Botón Guardar */}
        <FloatingButton
          className="save"
          onClick={isStoreAddMode ? handleAddToTicketFromCalculator : saveOrder}
          disabled={loading || !calculation}
        >
          Guardar
        </FloatingButton>
      </MobileActionButtons>
      
      {/* Material Selection Modal */}
      <MaterialModal show={materialModal.show} onClick={closeMaterialModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 style={{ margin: 0, fontSize: '18px' }}>
              Seleccionar {materialModal.type}
            </h3>
            <button 
              onClick={closeMaterialModal}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#666',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <SearchInput 
              placeholder={`Buscar ${materialModal.type}...`}
              onChange={(e) => {
                const searchTerm = e.target.value.toLowerCase();
                // Filter products based on search term
                setSearchTerm(searchTerm);
              }}
            />
            
            {/* Get products for the selected type */}
            {(() => {
              if (!materialModal.type) return null;
              
              let productList = [];
              const typeMap = {
                'moldura': 'molduras',
                'passepartout': 'passepartout', // Note: singular in API response
                'cristal': 'cristales',
                'trasera': 'traseras',
                'accesorio': 'accesorios'
              };
              
              const productKey = typeMap[materialModal.type];
              console.log('Looking for products in key:', productKey);
              console.log('Available product keys:', Object.keys(products));
              
              if (products[productKey]) {
                let allProducts = Array.isArray(products[productKey]) ? products[productKey] : Object.values(products[productKey]);
                
                // Filter products based on search term
                productList = searchTerm 
                  ? allProducts.filter(product => 
                      product.name?.toLowerCase().includes(searchTerm) ||
                      product.code?.toLowerCase().includes(searchTerm)
                    )
                  : allProducts;
              }
              
              console.log('Product list for', materialModal.type, ':', productList);
              if (productList.length > 0) {
                console.log('Sample product:', productList[0]);
              }
              
              if (productList.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    No hay {materialModal.type}s disponibles
                  </div>
                );
              }
              
              return productList.map((product) => (
                <MaterialOption 
                  key={product.id}
                  onClick={() => {
                    if (materialModal.type === 'passepartout') {
                      // For passepartout, we need to ask for window width
                      const width = prompt('Ancho de la ventana del passepartout (cm):', '0');
                      if (width) {
                        addMaterial(materialModal.type, product, { width: parseFloat(normalizeDecimal(width)) });
                      }
                    } else if (materialModal.type === 'accesorio') {
                      // For accessories, we might need quantity
                      const quantity = prompt('Cantidad:', '1');
                      if (quantity) {
                        addMaterial(materialModal.type, product, { quantity: parseInt(quantity) });
                      }
                    } else {
                      addMaterial(materialModal.type, product);
                    }
                  }}
                >
                  <div className="name">{product.name}</div>
                  <div className="details">
                    <span>{product.reference || product.code || product.barcode || '-'}</span>
                    <span className="price">
                      {(() => {
                        const price = product.basePrice || product.pricePerUnit || product.price || product.salePrice || 0;
                        console.log('Product price for', product.name, ':', price, 'Full product:', product);
                        return price;
                      })()}€
                    </span>
                  </div>
                </MaterialOption>
              ));
            })()}
          </div>
        </div>
      </MaterialModal>
      
      {mode === 'store' && (
        <TpvDrawer
          isOpen={true}
          onClose={() => setMode('professional')}
          calculation={calculation}
          formData={formData}
          externalAddToTicketSignal={addToTicketSignal}
          onTicketItemsChange={setTicketItems}
          onPaymentSuccess={() => {
            setCalculation(null);
            setTicketItems([]);
            setFormData({
              clientId: '',
              measurementType: 'INT',
              width: '',
              height: '',
              units: 1,
              discount: 0,
              clientReference: '',
              supplierReference: '',
              notes: ''
            });
            setMolduras([]);
            setPassepartouts([]);
            setCristales([]);
            setTraseras([]);
            setAccesorios([]);
            toast.success('Pago procesado y pedido creado correctamente');
          }}
          staticMode={true}
        />
      )}
      </MobileContainer>
    </>
  );
};

export default CalculatorMobile;
